import React from 'react';
import UserRow from 'features/activity/components/UserRow';
import TableSkeleton from 'shared/components/ui/TableSkeleton';
import { DEFAULT_COLUMNS, AVAILABLE_COLUMNS } from 'features/activity/config/userColumns';
import {
  TABLE_STYLES,
  tableHeaderRowClass,
  tableHeaderCellClass,
} from 'features/activity/styles/dataTableStyles';
import TableFooter from 'shared/components/tables/TableFooter';

/**
 * UsersTable — table-only. Loading/error/empty get tight, theme-aware cards.
 * The parent owns the "X of Y" footer and Load more button so that row lives
 * in lockstep with the rest of the activity pages (Events / Sessions /
 * Agent runs). Pass `hideLoadMore` when the parent renders the button.
 */
const UsersTable = ({
  users,
  loading,
  loadingMore = false,
  error,
  hasMore = false,
  onLoadMore,
  hasSearchFilter,
  searchTerm,
  columnConfig,
  onRowClick,
  hideLoadMore = false,
}) => {
  const config = columnConfig || DEFAULT_COLUMNS;
  const visibleOrder = (config.order || []).filter(key =>
    (config.enabled || []).includes(key) && AVAILABLE_COLUMNS[key]
  );
  const styles = TABLE_STYLES;

  const shellClass =
    'overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface';

  if (loading) {
    // The real column labels, so the skeleton's widths are the table's widths
    // and nothing shifts when rows arrive.
    return (
      <TableSkeleton
        columns={visibleOrder.map((key) => AVAILABLE_COLUMNS[key]?.label || key)}
        rows={10}
      />
    );
  }

  if (error) {
    return (
      <div className={shellClass}>
        <div className="px-6 py-8 text-center text-[12px]" style={{ color: 'var(--feedback-error)', fontFamily: 'Inter, sans-serif' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className={shellClass}>
        <div className="px-6 py-8 text-center text-[12px] text-[color:var(--text-secondary)]" style={{ fontFamily: 'Inter, sans-serif' }}>
          {hasSearchFilter && searchTerm ? 'No users found matching your search.' : 'No users found.'}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
            <thead>
              <tr className={tableHeaderRowClass}>
                {visibleOrder.map((key) => {
                  const col = AVAILABLE_COLUMNS[key];
                  const label = col ? col.label : key;
                  return (
                    <th key={key} className={`${tableHeaderCellClass}${col?.align === 'right' ? ' text-right' : ''}`}>
                      {label}
                    </th>
                  );
                })}
                <th className={tableHeaderCellClass} />
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const userId = user.distinctId || user.userId;
                return (
                  <UserRow
                    key={`${userId}-${index}`}
                    user={user}
                    onRowClick={onRowClick}
                    columns={visibleOrder}
                    styles={styles}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {!hideLoadMore && !loading && !error ? (
        <TableFooter
          shown={users.length}
          hasMore={hasMore}
          loading={loadingMore}
          onLoadMore={onLoadMore}
        />
      ) : null}
    </>
  );
};

export default UsersTable;
